import React, {useEffect, useState} from 'react'
import {getJobApplications, getOpenJobPosts} from '../../../api/HiringAPI'
import {useNavigate} from 'react-router-dom'
import ListPagination from '../../components/ListPagination'

type JobPostOption = { value: string; label: string }

const HiringApplications: React.FC = () => {
  const [jobs, setJobs] = useState<JobPostOption[]>([])
  const [selectedJobId, setSelectedJobId] = useState<string>('')
  const [rows, setRows] = useState<any[]>([])
  const [total, setTotal] = useState<number>(0)
  const [page, setPage] = useState<number>(1)
  const [limit, setLimit] = useState<number>(10)
  const [loading, setLoading] = useState<boolean>(false)
  const navigate = useNavigate()

  useEffect(() => {
    let mounted = true
    ;(async () => {
      const res = await getOpenJobPosts()
      const options: JobPostOption[] = (res?.data?.data || res?.data || []).map((j: any) => ({
        value: j.id,
        label: `${j.title} — ${j.department}`,
      }))
      if (!mounted) return
      setJobs(options)
      if (options.length && !selectedJobId) setSelectedJobId(options[0].value)
    })()
    return () => {
      mounted = false
    }
  }, [])

  useEffect(() => {
    if (!selectedJobId) return
    let mounted = true
    setLoading(true)
    ;(async () => {
      const offset = (page - 1) * limit
      const res = await getJobApplications(selectedJobId, { limit, offset, sortBy: 'appliedDate', sortOrder: 'desc' })
      if (!mounted) return
      const list = res?.data || []
      const pagination = res?.pagination || { total: list.length }
      setRows(list)
      setTotal(pagination.total)
      setLoading(false)
    })()
    return () => {
      mounted = false
    }
  }, [selectedJobId, page, limit])

  return (
    <div className='card'>
      <div className='card-header d-flex align-items-center gap-4'>
        <h3 className='card-title fw-bold mb-0'>Hiring — Applications</h3>
        <div className='ms-auto d-flex align-items-center gap-3'>
          <button
            className='btn btn-light'
            onClick={() => {
              window.history.pushState({}, '', '/hiring/jobs')
              window.dispatchEvent(new PopStateEvent('popstate'))
            }}
          >
            Back to Job Posts
          </button>
          <select className='form-select' value={selectedJobId} onChange={(e) => { setSelectedJobId(e.target.value); setPage(1) }}>
            {jobs.map((j) => (
              <option key={j.value} value={j.value}>{j.label}</option>
            ))}
          </select>
        </div>
      </div>
      <div className='card-body p-5'>
        <div className='table-responsive'>
          <table className='table align-middle table-row-dashed table-striped mb-0'>
            <thead>
              <tr className='text-muted fw-semibold text-uppercase'>
                <th className='min-w-200px'>Candidate</th>
                <th className='min-w-150px'>Contact</th>
                <th className='min-w-150px'>Experience</th>
                <th className='min-w-150px'>Company / Role</th>
                <th className='min-w-125px'>Scores</th>
                <th className='min-w-100px'>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className='text-center py-10'>Loading...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} className='text-center py-10'>No applications found</td></tr>
              ) : (
                rows.map((app: any) => (
                  <tr key={app.id} onClick={() => navigate(`/hiring/applications/${app.id}`)} style={{cursor:'pointer'}}>
                    <td>
                      <div className='d-flex flex-column'>
                        <span className='fw-bold'>{app.candidate.firstName} {app.candidate.lastName}</span>
                        <span className='text-muted'>{app.jobPost.title} — {app.jobPost.department}</span>
                      </div>
                    </td>
                    <td>
                      <div className='d-flex flex-column'>
                        <span>{app.candidate.email}</span>
                        <span className='text-muted'>{app.candidate.phone || '-'}</span>
                      </div>
                    </td>
                    <td>{app.candidate.experience ?? '-'}</td>
                    <td>
                      <div className='d-flex flex-column'>
                        <span>{app.candidate.currentCompany || '-'}</span>
                        <span className='text-muted'>{app.candidate.currentRole || '-'}</span>
                      </div>
                    </td>
                    <td>
                      <div className='d-flex flex-column'>
                        <span>Overall: {app.overallScore ?? '-'}</span>
                        <span className='text-muted'>Resume: {app.resumeScore ?? '-'} | Skill: {app.skillMatchScore ?? '-'}</span>
                      </div>
                    </td>
                    <td>
                      <span className='badge badge-light-primary'>{app.status}</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className='card-footer'>
        <ListPagination
          page={page}
          pageSize={limit}
          totalRecords={total}
          disabled={loading}
          onPageChange={setPage}
          onPageSizeChange={(size) => { setLimit(size); setPage(1) }}
        />
      </div>
    </div>
  )
}

export default HiringApplications


